import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { useViewerSettings } from '@/hooks/useViewerSettings';
import { eventToChord, formatChord, isModifierOnly } from '@/lib/shortcuts/chords';
import { DEFAULT_KEYBOARD_SHORTCUTS, SHORTCUT_ACTIONS, SHORTCUT_CATEGORY_ORDER, } from '@/lib/shortcuts/shortcutActions';
import { RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
const CATEGORY_LABEL_KEY = {
    tools: 'shortcuts.category.tools',
    transform: 'shortcuts.category.transform',
    layout: 'shortcuts.category.layout',
    navigation: 'shortcuts.category.navigation',
    playback: 'shortcuts.category.playback',
    panels: 'shortcuts.category.panels',
    report: 'shortcuts.category.report',
};
export function ShortcutsTab() {
    const { t } = useTranslation('settings');
    const { settings, updateKeyboardShortcuts, resetKeyboardShortcuts } = useViewerSettings();
    const bindings = settings.keyboardShortcuts ?? DEFAULT_KEYBOARD_SHORTCUTS;
    const [recordingId, setRecordingId] = useState(null);
    const [conflictNote, setConflictNote] = useState(null);
    // While recording, the next non-modifier keypress becomes the binding.
    // Capture-phase + stopImmediatePropagation so the viewer's own shortcut
    // handler doesn't also react to the key being recorded.
    useEffect(() => {
        if (!recordingId)
            return;
        const onKeyDown = (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (e.key === 'Escape') {
                setRecordingId(null);
                return;
            }
            if (isModifierOnly(e))
                return;
            const chord = eventToChord(e);
            const existing = Object.entries(bindings).find(([id, c]) => c === chord && id !== recordingId);
            if (existing) {
                // Reassign: clear the previous owner so a chord maps to one action.
                const prevId = existing[0];
                updateKeyboardShortcuts({ [recordingId]: chord, [prevId]: '' });
                const prevDef = SHORTCUT_ACTIONS.find((a) => a.id === prevId);
                setConflictNote(t('shortcuts.reassigned', {
                    chord: formatChord(chord),
                    action: prevDef ? t(prevDef.labelKey) : prevId,
                }));
            }
            else {
                updateKeyboardShortcuts({ [recordingId]: chord });
                setConflictNote(null);
            }
            setRecordingId(null);
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [recordingId, bindings, updateKeyboardShortcuts, t]);
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex items-start justify-between gap-4", children: [_jsx("p", { className: "text-muted-foreground text-sm", children: t('shortcuts.description') }), _jsxs(Button, { variant: "outline", size: "sm", onClick: () => {
                            resetKeyboardShortcuts();
                            setConflictNote(null);
                            setRecordingId(null);
                        }, className: "shrink-0", children: [_jsx(RotateCcw, { size: 14, className: "mr-1.5" }), t('shortcuts.resetAll')] })] }), _jsx("p", { className: "hidden text-xs text-amber-300/90 pointer-coarse:block", "data-testid": "shortcuts-keyboard-note", children: t('shortcuts.needsKeyboard') }), conflictNote && _jsx("div", { className: "text-xs text-amber-400", children: conflictNote }), SHORTCUT_CATEGORY_ORDER.map((category) => {
                const actions = SHORTCUT_ACTIONS.filter((a) => a.category === category);
                if (actions.length === 0)
                    return null;
                return (_jsxs("div", { className: "bg-muted/30 space-y-2 rounded-lg p-4", children: [_jsx("h3", { className: "text-foreground mb-2 text-sm font-medium", children: t(CATEGORY_LABEL_KEY[category]) }), actions.map((action) => {
                            const chord = bindings[action.id];
                            const isRecording = recordingId === action.id;
                            const isDefault = chord === DEFAULT_KEYBOARD_SHORTCUTS[action.id];
                            return (_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1", children: [_jsx("span", { className: "text-foreground text-sm", children: t(action.labelKey) }), _jsxs("div", { className: "flex items-center gap-2", children: [!isDefault && !isRecording && (_jsx("button", { type: "button", onClick: () => updateKeyboardShortcuts({
                                                    [action.id]: DEFAULT_KEYBOARD_SHORTCUTS[action.id],
                                                }), className: "text-muted-foreground hover:text-foreground -m-1.5 p-1.5 text-xs pointer-coarse:p-3", title: t('shortcuts.resetOne'), "aria-label": t('shortcuts.resetOne'), children: _jsx(RotateCcw, { size: 12 }) })), isRecording && (_jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "h-7 px-2 text-xs pointer-coarse:h-10", onClick: () => setRecordingId(null), children: t('shortcuts.cancelRecording') })), _jsx("button", { type: "button", onClick: () => {
                                                    setConflictNote(null);
                                                    setRecordingId(isRecording ? null : action.id);
                                                }, className: `min-w-[7rem] rounded border px-3 py-1.5 text-center font-mono text-xs transition-colors pointer-coarse:py-2.5 ${isRecording
                                                    ? 'border-primary text-primary animate-pulse'
                                                    : 'border-border bg-background text-foreground hover:border-primary/50'}`, children: isRecording
                                                    ? t('shortcuts.pressKey')
                                                    : chord
                                                        ? formatChord(chord)
                                                        : t('shortcuts.unbound') })] })] }, action.id));
                        })] }, category));
            })] }));
}
